import { useLocation } from "wouter";
import { examStageForGrade } from "@shared/territories";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, ClipboardList, Globe2, RefreshCw } from "lucide-react";

// Data moat: where the child sits on the national ability scale, built from the
// archive of Jamaican student answers.
export interface AbilityEstimate {
  percentile: number;
  theta: number;
  se: number;
  responses: number;
  source: "irt" | "mastery-fallback" | "none";
}

// A topic the child knew well that the forgetting curve says is fading.
export interface ReviewDueTopic {
  subject: string;
  topic: string;
  topicKey: string;
  mastery: number;
  daysSince: number;
  halfLifeDays: number;
  retention: number;
}

export type MasteryBand =
  | "not-started"
  | "early"
  | "needs-work"
  | "developing"
  | "strong"
  | "mastered";

const BAND_LABELS: Record<string, string> = {
  "not-started": "Not started",
  early: "Early days",
  "needs-work": "Needs work",
  developing: "Developing",
  strong: "Strong",
  mastered: "Mastered",
};

const BAND_TONES: Record<string, string> = {
  "not-started": "bg-muted text-muted-foreground",
  early: "bg-muted text-muted-foreground",
  "needs-work": "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  developing: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  strong: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  mastered: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
};

/**
 * /learning expects the canonical subject key ("math", "english", ...), the same value the
 * Study Plan and Dashboard pass. The moat gives us the display name, so map it back.
 */
export function canonicalSubjectKey(name: string): string {
  const lower = (name || "").toLowerCase().trim();
  if (lower === "mathematics" || lower === "math") return "math";
  if (lower === "language arts" || lower === "english" || lower === "english a") return "english";
  return lower;
}

/** Same practice link the Study Plan uses: /learning reads subject + topic off the query string. */
export function practiceUrl(subject: string, topicKey: string): string {
  return `/learning?subject=${encodeURIComponent(canonicalSubjectKey(subject))}&topic=${encodeURIComponent(topicKey)}`;
}

export function lastPractisedLabel(daysSince: number): string {
  if (daysSince <= 0) return "Last practised today";
  if (daysSince === 1) return "Last practised 1 day ago";
  return `Last practised ${daysSince} days ago`;
}

function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/** Compact mastery cell: the Bayesian mastery estimate plus its colour-coded band. */
export function TopicMasteryBadge({
  mastery,
  band,
  testId,
}: {
  mastery: number | null | undefined;
  band: string | null | undefined;
  testId?: string;
}) {
  if (typeof mastery !== "number" && !band) {
    return <span className="text-xs text-muted-foreground">Not yet</span>;
  }
  const tone = (band && BAND_TONES[band]) || "bg-muted text-muted-foreground";
  const label = band ? BAND_LABELS[band] ?? band : null;
  return (
    <Badge
      variant="secondary"
      className={`text-xs font-medium whitespace-nowrap ${tone}`}
      title={typeof mastery === "number" ? `Mastery ${mastery}%` : undefined}
      data-testid={testId}
    >
      {typeof mastery === "number" ? `${Math.round(mastery)}%` : ""}
      {typeof mastery === "number" && label ? " · " : ""}
      {label ?? ""}
    </Badge>
  );
}

/** Compact national cell: where this topic sits against Jamaican students on EduFocal. */
export function TopicNationalStanding({
  percentile,
  testId,
}: {
  percentile: number | null | undefined;
  testId?: string;
}) {
  if (typeof percentile !== "number") {
    return (
      <span className="text-xs text-muted-foreground" data-testid={testId}>
        Not yet
      </span>
    );
  }
  const label = percentile >= 60 ? `Top ${Math.max(1, 100 - percentile)}%` : `${ordinal(percentile)} percentile`;
  return (
    <span
      className="text-xs font-medium text-primary whitespace-nowrap"
      title={`Better than ${percentile}% of Jamaican students on this topic`}
      data-testid={testId}
    >
      {label}
    </span>
  );
}

/**
 * The two data-moat cards students see on their progress report: where they stand
 * nationally, and which topics the forgetting curve says are fading.
 */
export function MoatCards({
  ability,
  reviewDue,
  grade,
}: {
  ability: AbilityEstimate | null | undefined;
  reviewDue: ReviewDueTopic[] | null | undefined;
  grade?: number | null;
}) {
  const [, setLocation] = useLocation();
  const hasAbility = !!ability && ability.source !== "none";
  const dueTopics = Array.isArray(reviewDue) ? reviewDue : [];

  // Only primary-exam grades sit PEP, so only they are asked for PEP results. A Grade 11 student
  // was being asked to report a test they sat five years ago, or never sat at all.
  const isPrimaryExam = examStageForGrade(grade) === "PRIMARY";
  const pepPrompt =
    grade === 6
      ? "Sat PEP already? Sharing your results helps us learn what practice really works."
      : "Sat your PEP test already? Sharing your results helps us learn what practice really works.";

  return (
    <>
      <Card
        className="border-primary/30 animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both"
        data-testid="card-national-standing"
      >
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
              <Globe2 className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base">Where you stand in Jamaica</CardTitle>
              <p className="text-xs text-muted-foreground">
                Compared with Jamaican students who learn on EduFocal
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {hasAbility && ability ? (
            <div className="flex items-center gap-4 flex-wrap">
              <div
                className="text-4xl sm:text-5xl font-bold text-primary leading-none"
                data-testid="text-ability-percentile"
              >
                {ability.percentile}%
              </div>
              <div className="flex-1 min-w-[14rem]">
                <p className="text-sm font-medium">
                  You are doing better than {ability.percentile}% of Jamaican students on EduFocal
                </p>
                <Progress value={ability.percentile} className="h-2 mt-2" />
                {ability.source === "mastery-fallback" ? (
                  <p className="text-xs text-muted-foreground mt-2" data-testid="text-ability-confidence">
                    Early estimate. Answer more questions to sharpen it.
                  </p>
                ) : ability.responses > 0 ? (
                  <p className="text-xs text-muted-foreground mt-2">
                    Based on {ability.responses} of your answers so far
                  </p>
                ) : null}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="text-ability-empty">
              Answer 5 or more questions and we will show where you stand nationally.
            </p>
          )}
          {isPrimaryExam && (
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLocation("/pep-results")}
                data-testid="button-add-pep-results"
              >
                <ClipboardList className="h-4 w-4 mr-1" />
                Add your PEP results
              </Button>
              <p className="text-xs text-muted-foreground flex-1 min-w-[12rem]">{pepPrompt}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card
        className="border-blue-200 dark:border-blue-900/50 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-100 fill-mode-both"
        data-testid="card-review-due"
      >
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <RefreshCw className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base">Time to review</CardTitle>
              <p className="text-xs text-muted-foreground">
                You knew these well. A quick practice will lock them back in.
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {dueTopics.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="text-review-due-empty">
              Nothing to review right now. Nice!
            </p>
          ) : (
            <div className="space-y-2">
              {dueTopics.map((item) => {
                const retentionPercent = Math.max(0, Math.min(100, Math.round(item.retention * 100)));
                return (
                  <div
                    key={`${item.subject}-${item.topicKey}`}
                    className="flex items-start gap-3 p-3 rounded-md bg-blue-50 dark:bg-blue-950/30 flex-wrap"
                    data-testid={`row-review-${item.topicKey}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{item.topic}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.subject} &middot; {lastPractisedLabel(item.daysSince)}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <div className="h-1.5 w-28 sm:w-40 rounded-full bg-blue-200/70 dark:bg-blue-900/60 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-blue-500 dark:bg-blue-400"
                            style={{ width: `${retentionPercent}%` }}
                          />
                        </div>
                        <span className="text-[11px] text-muted-foreground">
                          {retentionPercent < 10 ? "Mostly faded, time for a refresher" : `${retentionPercent}% still fresh`}
                        </span>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="flex-shrink-0"
                      onClick={() => setLocation(practiceUrl(item.subject, item.topicKey))}
                      data-testid={`button-practise-${item.topicKey}`}
                    >
                      Practise <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}

export default MoatCards;
